import { useState, useEffect } from "react";
import ReactSelect from "react-select";
import FormActions from "../../../../components/Form/FormActions";
import { COLORS } from "../../../../constants";
import { metaColetivaCreateSchema } from "../../../../lib/validations";
import { z } from "zod";
import {
  MetaColetivaFormData,
  calculateMetaAtingida,
  applyNotaMask,
  applyTimeMask,
} from "../context/MetaColetivaContext";

interface MetaColetivaFormProps {
  initialData?: any;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  lojas: Array<{ id: number; nome: string }>;
}

const MetaColetivaForm = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
  lojas,
}: MetaColetivaFormProps) => {
  const [formData, setFormData] = useState<MetaColetivaFormData>({
    inicializacaoNota: initialData?.inicializacaoNota?.toString() || "",
    producaoNota: initialData?.producaoNota?.toString() || "",
    finalizacaoNota: initialData?.finalizacaoNota?.toString() || "",
    tempoMeta: initialData?.tempoMeta || "",
    tempoReal: initialData?.tempoReal || "",
    errosProducao: initialData?.errosProducao?.toString() || "",
    metaAtingida: initialData?.metaAtingida?.toString() || "",
    observacao: initialData?.observacao || "",
    lojaId: initialData?.lojaId?.toString() || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        inicializacaoNota: initialData?.inicializacaoNota?.toString() || "",
        producaoNota: initialData?.producaoNota?.toString() || "",
        finalizacaoNota: initialData?.finalizacaoNota?.toString() || "",
        tempoMeta: initialData?.tempoMeta || "",
        tempoReal: initialData?.tempoReal || "",
        errosProducao: initialData?.errosProducao?.toString() || "",
        metaAtingida: initialData?.metaAtingida?.toString() || "",
        observacao: initialData?.observacao || "",
        lojaId: initialData?.lojaId?.toString() || "",
      });
    }
  }, [initialData]);

  useEffect(() => {
    const newMetaAtingida = calculateMetaAtingida(formData);
    if (newMetaAtingida !== formData.metaAtingida) {
      setFormData((prev) => ({ ...prev, metaAtingida: newMetaAtingida }));
    }
  }, [
    formData,
    formData.inicializacaoNota,
    formData.producaoNota,
    formData.finalizacaoNota,
    formData.tempoMeta,
    formData.tempoReal,
  ]);

  const handleChange = (name: string, value: any) => {
    if (
      name === "inicializacaoNota" ||
      name === "producaoNota" ||
      name === "finalizacaoNota"
    ) {
      const maskedValue = applyNotaMask(value);
      setFormData((prev) => ({ ...prev, [name]: maskedValue }));
    } else if (name === "tempoMeta" || name === "tempoReal") {
      const maskedValue = applyTimeMask(value);
      setFormData((prev) => ({ ...prev, [name]: maskedValue }));
    } else if (name !== "metaAtingida") {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const validatedData = metaColetivaCreateSchema.parse({
        lojaId: parseInt(formData.lojaId),
        inicializacaoNota: parseFloat(formData.inicializacaoNota),
        producaoNota: parseFloat(formData.producaoNota),
        finalizacaoNota: parseFloat(formData.finalizacaoNota),
        errosProducao: parseInt(formData.errosProducao),
        metaAtingida: parseFloat(
          formData.metaAtingida || calculateMetaAtingida(formData)
        ),
        tempoMeta: formData.tempoMeta,
        tempoReal: formData.tempoReal,
        observacao: formData.observacao,
      });

      setErrors({});
      await onSubmit(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.issues.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
      }
    }
  };

  const lojaOptions = lojas.map((loja) => ({
    value: loja.id.toString(),
    label: loja.nome,
  }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700">
          Loja
        </label>
        <ReactSelect
          options={lojaOptions}
          value={
            lojaOptions.find((option) => option.value === formData.lojaId) ||
            null
          }
          onChange={(option) => handleChange("lojaId", option?.value || "")}
          placeholder="Selecione uma loja"
          isClearable
          styles={{
            control: (base) => ({
              ...base,
              borderColor: errors.lojaId ? COLORS.ERROR : COLORS.NEUTRAL_300,
              "&:hover": {
                borderColor: errors.lojaId ? COLORS.ERROR : COLORS.PRIMARY,
              },
              "&:focus-within": {
                borderColor: errors.lojaId ? COLORS.ERROR : COLORS.PRIMARY,
                boxShadow: `0 0 0 2px ${errors.lojaId ? "rgba(239, 68, 68, 0.2)" : "rgba(147, 32, 24, 0.2)"}`,
              },
            }),
          }}
        />
        {errors.lojaId && (
          <p className="mt-1 text-sm text-red-500">{errors.lojaId}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Nota Inicialização (0-10)
          </label>
          <input
            type="text"
            value={formData.inicializacaoNota}
            onChange={(e) => handleChange("inicializacaoNota", e.target.value)}
            placeholder="0.0"
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.inicializacaoNota
                ? "border-red-500 focus:ring-red-500"
                : "border-neutral-300 focus:ring-primary"
            }`}
          />
          {errors.inicializacaoNota && (
            <p className="mt-1 text-sm text-red-500">
              {errors.inicializacaoNota}
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Nota Produção (0-10)
          </label>
          <input
            type="text"
            value={formData.producaoNota}
            onChange={(e) => handleChange("producaoNota", e.target.value)}
            placeholder="0.0"
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.producaoNota
                ? "border-red-500 focus:ring-red-500"
                : "border-neutral-300 focus:ring-primary"
            }`}
          />
          {errors.producaoNota && (
            <p className="mt-1 text-sm text-red-500">{errors.producaoNota}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Nota Finalização (0-10)
          </label>
          <input
            type="text"
            value={formData.finalizacaoNota}
            onChange={(e) => handleChange("finalizacaoNota", e.target.value)}
            placeholder="0.0"
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.finalizacaoNota
                ? "border-red-500 focus:ring-red-500"
                : "border-neutral-300 focus:ring-primary"
            }`}
          />
          {errors.finalizacaoNota && (
            <p className="mt-1 text-sm text-red-500">
              {errors.finalizacaoNota}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Tempo Meta
          </label>
          <input
            type="text"
            value={formData.tempoMeta}
            onChange={(e) => handleChange("tempoMeta", e.target.value)}
            placeholder="00:00:00"
            maxLength={8}
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.tempoMeta
                ? "border-red-500 focus:ring-red-500"
                : "border-neutral-300 focus:ring-primary"
            }`}
          />
          {errors.tempoMeta && (
            <p className="mt-1 text-sm text-red-500">{errors.tempoMeta}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Tempo Real
          </label>
          <input
            type="text"
            value={formData.tempoReal}
            onChange={(e) => handleChange("tempoReal", e.target.value)}
            placeholder="00:00:00"
            maxLength={8}
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.tempoReal
                ? "border-red-500 focus:ring-red-500"
                : "border-neutral-300 focus:ring-primary"
            }`}
          />
          {errors.tempoReal && (
            <p className="mt-1 text-sm text-red-500">{errors.tempoReal}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Erros Produção
          </label>
          <input
            type="number"
            value={formData.errosProducao}
            onChange={(e) => handleChange("errosProducao", e.target.value)}
            placeholder="0"
            min="0"
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.errosProducao
                ? "border-red-500 focus:ring-red-500"
                : "border-neutral-300 focus:ring-primary"
            }`}
          />
          {errors.errosProducao && (
            <p className="mt-1 text-sm text-red-500">{errors.errosProducao}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Meta Atingida %
          </label>
          <input
            type="text"
            value={formData.metaAtingida}
            readOnly
            className="w-full cursor-not-allowed rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-neutral-600"
          />
          <p className="mt-1 text-xs text-neutral-500">
            Calculado com base nas notas e tempos informados
          </p>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700">
          Observação
        </label>
        <textarea
          value={formData.observacao}
          onChange={(e) => handleChange("observacao", e.target.value)}
          placeholder="Observações adicionais..."
          rows={3}
          className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
            errors.observacao
              ? "border-red-500 focus:ring-red-500"
              : "border-neutral-300 focus:ring-primary"
          }`}
        />
        {errors.observacao && (
          <p className="mt-1 text-sm text-red-500">{errors.observacao}</p>
        )}
      </div>

      <FormActions
        onCancel={onCancel}
        isLoading={isLoading}
        submitText={initialData ? "Atualizar" : "Criar"}
      />
    </form>
  );
};

export default MetaColetivaForm;
